import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { forgotPassword } from '../api/auth';
import { toFormErrors, type FormErrors } from '../auth/errors';
import { Alert, Button, Input } from '../components/ui';
import { AuthLayout } from './AuthLayout';

const NO_ERRORS: FormErrors = { fields: {}, message: null };

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<FormErrors>(NO_ERRORS);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setErrors(NO_ERRORS);
    setIsSubmitting(true);

    try {
      await forgotPassword(email);
      setSent(true);
    } catch (error) {
      setErrors(toFormErrors(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="Tell us the address on your account and we will send you a link."
      footer={
        <>
          Remembered it? <Link to="/login">Sign in</Link>
        </>
      }
    >
      {sent ? (
        // Deliberately non-committal: the response is the same whether or not
        // the address has an account, so this screen must be too.
        <Alert tone="success" title="Check your inbox">
          If that address has an account, a reset link is on its way. The link works once and
          expires shortly.
        </Alert>
      ) : (
        <form className="sc-form" onSubmit={handleSubmit} noValidate>
          {errors.message && <Alert tone="danger">{errors.message}</Alert>}

          <Input
            label="Email"
            type="email"
            name="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            error={errors.fields['email']}
          />

          <div className="sc-form__actions">
            <Button type="submit" isLoading={isSubmitting} loadingLabel="Sending link" fullWidth>
              Send reset link
            </Button>
          </div>
        </form>
      )}
    </AuthLayout>
  );
}
